"use client";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";
import Icon, {IconNameExtended} from "@/components/library/Icon";
import TextArea from "@/components/library/TextArea";
import { CampaignPurpose, PURPOSE_FOCUS_MAP } from "@/constants/onboarding";
import { useOrg } from "@/hooks/useOrg";
import { PhoneScript } from "@/types/campaigns";
import { UseCase } from "@/types/scripts";
import { apiLive, apiFetch } from "@/lib/api";
import { createMinDurationEnforcer } from "@/utils/onboarding/common";
import { loadAssociatedUseCases } from "@/utils/onboarding/onboardingScriptApi";
import { scriptApiVersionFromFeatures } from "@/utils/org/scriptsApiVersion";
import { getScriptDetailUrl } from "@/utils/scriptVersioning/api";
import { OnboardingStepHeader } from "./OnboardingStepHeader";
import { ScriptBuildingLoader } from "./ScriptBuildingLoader";
import { ScriptPreviewSection } from "./ScriptPreviewSection";
import { StepFooter } from "./StepFooter";

interface OutboundScriptStepProps {
  readonly onBack?: () => void;
  readonly onNext: () => void;
}

type ScriptGenerationResponse = {
  success: boolean;
  campaignId?: string;
  scriptId?: string;
  script?: PhoneScript;
  useCases?: UseCase[];
  message?: string;
};

type SetupStatus =
  | { state: "selectPurpose" }
  | { state: "loading" }
  | { state: "ready"; script: PhoneScript; useCases: UseCase[]; scriptId: string }
  | { state: "error"; message?: string };

const OUTBOUND_SCRIPT_WORKSTREAM = [
  "loadBusinessProfile(config.org)",
  "analyzeTargetAudience(contacts)",
  "initOutboundTemplate({ type: 'outreach' })",
  "setCampaignObjective(config.purpose)",
  "composeOpeningPitch(profile, goals)",
  "buildObjectionHandlers(industry)",
  "configureCallToAction(campaign)",
  "runComplianceChecks({ tcpa: true })",
  "validateConversationFlow(scriptDraft)",
  "optimizeTalkingPoints(pitch, handlers)",
  "auditCallScript(decisionTree)",
  "finalizeOutboundBundle()",
  "registerDialerHook(bundle)",
  "exportCampaignScript({ format: 'v1' })",
];

// Purpose options for display (same cards used in the old CampaignSetupStep)
const CAMPAIGN_PURPOSES: Array<{
  key: CampaignPurpose;
  icon: IconNameExtended;
  colorClass: string;
  bgClass: string;
}> = [
  {
    key: "sales",
    icon: "arrow-trend-up",
    colorClass: "text-blue-600 dark:text-blue-400",
    bgClass: "bg-blue-100 dark:bg-blue-900/30",
  },
  {
    key: "renewals",
    icon: "arrows-rotate",
    colorClass: "text-emerald-600 dark:text-emerald-400",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/30",
  },
  {
    key: "latePays",
    icon: "clock",
    colorClass: "text-amber-600 dark:text-amber-400",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
  },
  {
    key: "missingDocs",
    icon: "file-lines",
    colorClass: "text-violet-600 dark:text-violet-400",
    bgClass: "bg-violet-100 dark:bg-violet-900/30",
  },
  {
    key: "custom",
    icon: "sliders",
    colorClass: "text-gray-600 dark:text-gray-400",
    bgClass: "bg-gray-100 dark:bg-gray-800/50",
  },
];

export function OutboundScriptStep({ onBack, onNext }: OutboundScriptStepProps) {
  const { t } = useTranslation("onboarding");
  const { org, saveOrg } = useOrg();

  const setupRef = useRef(false);
  const [status, setStatus] = useState<SetupStatus>({ state: "selectPurpose" });
  const [selectedPurpose, setSelectedPurpose] = useState<CampaignPurpose | null>(null);
  const [customDescription, setCustomDescription] = useState("");
  const [customDescriptionError, setCustomDescriptionError] = useState(false);

  const isOrgLoaded = org !== null && org !== undefined;
  const existingScriptId = org?.details?.onboarding?.outboundScriptId;
  const existingPurpose = org?.details?.onboarding?.campaignPurpose as CampaignPurpose | undefined;

  // Fetch an existing script and its use cases
  const fetchExistingScript = useCallback(async (scriptId: string): Promise<SetupStatus> => {
    const version = scriptApiVersionFromFeatures(org?.features);
    const existingScript = await apiLive<PhoneScript>(
      getScriptDetailUrl(scriptId, version)
    );

    if (!existingScript) {
      return {
        state: "error",
        message: "Failed to load existing script. Please try again.",
      };
    }

    const useCases = await loadAssociatedUseCases(existingScript, version);

    return {
      state: "ready",
      script: existingScript,
      useCases,
      scriptId,
    };
  }, [org?.features]);

  // Generate a new script via the API using the selected purpose
  const generateNewScript = useCallback(
    async (purpose: CampaignPurpose, description?: string): Promise<SetupStatus> => {
      const focusOptions = PURPOSE_FOCUS_MAP[purpose];

      const response = await apiFetch<ScriptGenerationResponse>(
        "/api/onboarding/outbound-scripts",
        "POST",
        {
        body: {
        focusOptions,
        campaignPurpose: purpose,
        skipCampaign: true,
        ...(description ? { customDescription: description } : {}),
      },
      },
      );

      if (!response?.success || !response.scriptId || !response.script || !response.useCases) {
        throw new Error(response?.message ?? "Failed to set up script");
      }

      // Save script ID and campaign purpose to org
      await saveOrg(
        {
          details: {
            ...org?.details,
            onboarding: {
              ...org?.details?.onboarding,
              status: org?.details?.onboarding?.status ?? "in_progress",
              maxCompletedStep: org?.details?.onboarding?.maxCompletedStep ?? 1,
              outboundScriptId: response.scriptId,
              campaignPurpose: purpose,
            },
          },
        },
        { silent: true }
      );

      return {
        state: "ready",
        script: response.script,
        useCases: response.useCases,
        scriptId: response.scriptId,
      };
    },
    [org, saveOrg]
  );

  // If an existing script was already generated, skip purpose selection and show it
  useEffect(() => {
    if (!isOrgLoaded) return;
    if (status.state !== "selectPurpose") return;
    if (setupRef.current) return;

    if (existingScriptId) {
      setupRef.current = true;
      setStatus({ state: "loading" });

      // Pre-select the existing purpose if available
      if (existingPurpose) {
        setSelectedPurpose(existingPurpose);
      }

      const loadExisting = async () => {
        const result = await fetchExistingScript(existingScriptId);
        setStatus(result);
      };
      loadExisting();
    }
  }, [isOrgLoaded, existingScriptId, existingPurpose, status.state, fetchExistingScript]);

  // Handle "Generate Script" after purpose is selected
  const handleGenerateScript = useCallback(async () => {
    if (!selectedPurpose) return;

    if (selectedPurpose === "custom" && !customDescription.trim()) {
      setCustomDescriptionError(true);
      return;
    }

    setupRef.current = true;
    setStatus({ state: "loading" });

    const enforceMinDuration = createMinDurationEnforcer(20_000);
    const description = selectedPurpose === "custom" ? customDescription.trim() : undefined;

    try {
      const result = await generateNewScript(selectedPurpose, description || undefined);
      await enforceMinDuration();
      setStatus(result);
    } catch (error) {
      console.error("Failed to set up outbound script:", error);
      await enforceMinDuration();
      setStatus({
        state: "error",
        message: error instanceof Error ? error.message : "Failed to set up script",
      });
    }
  }, [selectedPurpose, customDescription, generateNewScript]);

  const handleScriptUpdate = useCallback((updatedScript: PhoneScript) => {
    setStatus((prev) => {
      if (prev.state === "ready") {
        return { ...prev, script: updatedScript };
      }
      return prev;
    });
  }, []);

  const handleUseCasesUpdate = useCallback((updatedUseCases: UseCase[]) => {
    setStatus((prev) => {
      if (prev.state === "ready") {
        return { ...prev, useCases: updatedUseCases };
      }
      return prev;
    });
  }, []);

  const handleRetry = useCallback(() => {
    setupRef.current = false;
    setStatus({ state: "selectPurpose" });
  }, []);

  // =========================================================================
  // RENDER: Purpose selection
  // =========================================================================
  if (status.state === "selectPurpose") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-8">
          <div className="w-full max-w-4xl mx-auto space-y-6">
            <OnboardingStepHeader
              title={t("outboundScript.purposeTitle")}
              description={t("outboundScript.purposeDescription")}
            />

            {/* Purpose Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CAMPAIGN_PURPOSES.map(({ key, icon, colorClass, bgClass }) => (
                <Button
                  key={key}
                  type="none"
                  onClick={() => setSelectedPurpose(key)}
                  className={clsx(
                    "p-5 rounded-xl w-full",
                    "border-2 transition-all duration-200",
                    selectedPurpose === key
                      ? "border-primary bg-primary/5 shadow-sm"
                      : "border-200 hover:border-primary/50 hover:bg-50 dark:hover:bg-800/50"
                  )}
                >
                  <div className="flex items-start gap-4 text-left">
                    <div
                      className={clsx(
                        "w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0",
                        bgClass
                      )}
                    >
                      <Icon icon={icon} className={clsx("w-5 h-5", colorClass)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">
                        {t(`campaignSetup.purposes.${key}.title`)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5 whitespace-normal">
                        {t(`campaignSetup.purposes.${key}.description`)}
                      </p>
                    </div>
                    <div
                      className={clsx(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1",
                        selectedPurpose === key ? "border-primary bg-primary" : "border-300"
                      )}
                    >
                      {selectedPurpose === key && <Icon icon="circle-check" className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </Button>
              ))}
            </div>

            {/* Custom campaign description */}
            <AnimatePresence>
              {selectedPurpose === "custom" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <TextArea
                    label={t("outboundScript.customDescriptionLabel")}
                    placeholder={t("outboundScript.customDescriptionPlaceholder")}
                    value={customDescription}
                    onChange={(val) => {
                      setCustomDescription(val);
                      if (val.trim()) setCustomDescriptionError(false);
                    }}
                    isRequired
                    maxLength={1000}
                    showError={customDescriptionError}
                    helperText={customDescriptionError ? t("outboundScript.customDescriptionRequired") : undefined}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Info hint */}
            <div className="rounded-xl border border-200 bg-panel p-4">
              <div className="flex gap-3">
                <Icon icon="circle-info" className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm text-foreground">{t("outboundScript.purposeInfoTitle")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("outboundScript.purposeInfoDescription")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <StepFooter
          onBack={onBack}
          onNext={handleGenerateScript}
          nextLabel={t("outboundScript.generateScript")}
          nextDisabled={!selectedPurpose}
        />
      </div>
    );
  }

  // =========================================================================
  // RENDER: Loading state
  // =========================================================================
  if (status.state === "loading") {
    return (
      <ScriptBuildingLoader
        title={t("outboundScript.generatingTitle")}
        subtitle={t("outboundScript.generatingDescription")}
        messages={t("outboundScript.buildingLoader.messages", { returnObjects: true }) as string[]}
        codeLines={OUTBOUND_SCRIPT_WORKSTREAM}
        metricLabels={
          t("outboundScript.buildingLoader.metrics", { returnObjects: true }) as [
            string,
            string,
            string,
          ]
        }
      />
    );
  }

  // =========================================================================
  // RENDER: Error state
  // =========================================================================
  if (status.state === "error") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-8">
          <div className="w-full max-w-2xl mx-auto space-y-6">
            <OnboardingStepHeader
              title={t("outboundScript.title")}
              description={t("outboundScript.subtitle")}
            />

            <div
              className={clsx(
                "rounded-2xl border border-destructive/50 bg-destructive/5 p-6",
                "text-center space-y-4"
              )}
            >
              <div className="space-y-2">
                <h3 className="font-semibold text-destructive">
                  {t("outboundScript.error.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {status.message ?? t("outboundScript.error.generic")}
                </p>
              </div>
              <Button type="primary" onClick={handleRetry}>
                {t("outboundScript.error.retry")}
              </Button>
            </div>
          </div>
        </div>

        <StepFooter onBack={onBack} onSkip={onNext} onNext={onNext} />
      </div>
    );
  }

  // =========================================================================
  // RENDER: Ready state - show script preview
  // =========================================================================
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-8">
        <div className="w-full max-w-4xl mx-auto space-y-6">
          <OnboardingStepHeader
            title={t("outboundScript.title")}
            description={t("outboundScript.subtitle")}
          />

          <ScriptPreviewSection
            script={status.script}
            useCases={status.useCases}
            onScriptUpdate={handleScriptUpdate}
            onUseCasesUpdate={handleUseCasesUpdate}
            mode="outbound"
          />
        </div>
      </div>

      <StepFooter onBack={onBack} onSkip={onNext} onNext={onNext} />
    </div>
  );
}
